import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, setToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate("/login");
      return;
    }
    const sessionId = decodeURIComponent(match[1]);

    (async () => {
      try {
        // Pull the sharer's username stashed by VideoView when this
        // visitor arrived via `/v/{id}?ref=@name`. Fresh signups get
        // permanently attributed so the sharer earns 10% of every
        // future boost this new user's videos receive.
        let refUsername = null;
        try {
          const raw = localStorage.getItem("neosync_signup_ref");
          if (raw) {
            const parsed = JSON.parse(raw);
            // 30-day window — WhatsApp shares on Monday still credit
            // sharers when the friend finally signs up Friday.
            const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
            if (parsed?.ref && parsed?.saved_at &&
                (Date.now() - parsed.saved_at) < THIRTY_DAYS) {
              refUsername = String(parsed.ref).replace(/^@/, "").trim().toLowerCase();
            }
          }
        } catch { /* localStorage might be blocked in private mode */ }
        const res = await api.post(
          "/auth/session",
          refUsername ? { ref: refUsername } : {},
          { headers: { "X-Session-ID": sessionId } },
        );
        if (res.data.session_token) setToken(res.data.session_token);
        setUser(res.data.user);
        // Flag brand-new signups so the WhatsApp earnings popup shows once
        // right after their first login (consumed on the feed).
        try {
          if (res.data.is_new_user) localStorage.setItem("wa_new_user_prompt", "1");
        } catch { /* ignore */ }
        // Clean up the stashed ref — it's now attributed on the user doc.
        try { localStorage.removeItem("neosync_signup_ref"); } catch { /* ignore */ }
        // clean hash and go to feed
        window.history.replaceState({}, "", "/");
        navigate("/", { state: { user: res.data.user } });
      } catch (e) {
        console.error("Session exchange failed", e);
        navigate("/login");
      }
    })();
  }, [navigate, setUser]);

  return (
    <div
      data-testid="auth-callback-loading"
      className="min-h-screen bg-[#050505] flex items-center justify-center"
    >
      <div className="flex flex-col items-center gap-4">
        <div className="h-12 w-12 border-2 border-[#39FF14] border-t-transparent animate-spin" />
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/60">
          syncing identity
        </p>
      </div>
    </div>
  );
}
